// src/pages/SocialJoinEventPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import logo from '../assets/img/logo2.png';
import '../Auth/Home.css';
import { useTranslation } from '../i18n';

// ✅ Leaflet (sin react-leaflet, para no depender de componentes extra)
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix íconos en algunos builds
import marker2x from 'leaflet/dist/images/marker-icon-2x.png';
import marker1x from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: marker2x,
  iconUrl: marker1x,
  shadowUrl: markerShadow,
});

const SocialJoinEventPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [status, setStatus] = useState('idle'); // idle | processing | success | error
  const [message, setMessage] = useState('');

  // ✅ Vista previa (pública) del evento
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token') || '';
  const eventId = searchParams.get('eventId') || '';

  // Lo mismo que usas en el resto del sitio
  const userRole = localStorage.getItem('userRole') || '';
  const storedUserId =
    localStorage.getItem('userId') ||
    localStorage.getItem('_id') ||
    localStorage.getItem('user_id') ||
    '';

  const apiUrl = process.env.REACT_APP_API_URL || 'https://tzone-web-backend-1-2ev5.onrender.com';

  // =========================
  // ✅ MAPAS (Leaflet directo)
  // =========================
  const meetingMapElRef = useRef(null);
  const routeMapElRef = useRef(null);

  const meetingMapRef = useRef(null);
  const routeMapRef = useRef(null);

  // 1) Traer preview público del evento (SIN login)
  useEffect(() => {
    if (!eventId) return;

    let cancelled = false;

    const loadPreview = async () => {
      try {
        setPreviewError('');
        const resp = await fetch(`${apiUrl}/api/social/events/public/${eventId}`);
        const data = await resp.json().catch(() => ({}));

        if (!resp.ok) throw new Error(data.message || t('socialJoinEvent.previewLoadError'));

        if (!cancelled) setPreview(data.event || null);
      } catch (e) {
        if (!cancelled) setPreviewError(e.message || t('socialJoinEvent.previewLoadError'));
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, eventId]);

  // 2) Pintar mapa del punto (cuando llegue preview)
  useEffect(() => {
    if (!preview) return;
    if (!meetingMapElRef.current) return;

    // evita reinicialización
    if (meetingMapRef.current) return;

    const { lat, lon } = preview;
    const hasCoords = typeof lat === 'number' && typeof lon === 'number';

    // Si no hay coords, no inicializamos mapa
    if (!hasCoords) return;

    const map = L.map(meetingMapElRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const center = [lat, lon];
    map.setView(center, 14);
    L.marker(center).addTo(map);

    meetingMapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch (_) {}
      meetingMapRef.current = null;
    };
  }, [preview]);

  // 3) Pintar mapa del recorrido (GPX points) (cuando llegue preview)
  useEffect(() => {
    if (!preview) return;
    if (!routeMapElRef.current) return;

    if (routeMapRef.current) return;

    const points = Array.isArray(preview.routeGpxPoints) ? preview.routeGpxPoints : [];
    const bbox = preview.routeGpxBbox || null;

    if (!points.length && !bbox) return;

    const map = L.map(routeMapElRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // points vienen como [[lat, lon], ...]
    if (points.length) {
      const poly = L.polyline(points, { weight: 4 }).addTo(map);
      map.fitBounds(poly.getBounds(), { padding: [20, 20] });
    } else if (bbox && bbox.minLat != null && bbox.minLon != null && bbox.maxLat != null && bbox.maxLon != null) {
      const bounds = L.latLngBounds(
        [bbox.minLat, bbox.minLon],
        [bbox.maxLat, bbox.maxLon]
      );
      map.fitBounds(bounds, { padding: [20, 20] });
    }

    routeMapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch (_) {}
      routeMapRef.current = null;
    };
  }, [preview]);

  // =========================
  // ✅ Aceptar invitación (TU CÓDIGO ORIGINAL)
  // =========================
  useEffect(() => {
    // Si falta token o eventId, el enlace está roto
    if (!token || !eventId) {
      setStatus('error');
      setMessage(t('socialJoinEvent.invalidInviteLink'));
      return;
    }

    // Si aún no hay rol, el usuario no ha terminado de iniciar sesión
    if (!userRole) {
      setStatus('idle');
      setMessage('');
      return;
    }

    const acceptInvite = async () => {
      try {
        setStatus('processing');
        setMessage(t('socialJoinEvent.processingAttendance'));

        // Intentamos leer un posible token de autenticación de localStorage
        const rawToken =
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('jwtToken') ||
          localStorage.getItem('userToken') ||
          '';

        const headers = {
          'Content-Type': 'application/json',
        };

        if (rawToken) {
          headers.Authorization = rawToken.startsWith('Bearer ')
            ? rawToken
            : `Bearer ${rawToken}`;
          headers['x-auth-token'] = rawToken;
        }

        if (storedUserId) {
          // Fallback explícito para el middleware auth.js
          headers['x-user-id'] = storedUserId;
        }

        console.log('[SocialJoinEvent] accept-invite request', {
          hasToken: !!rawToken,
          hasUserIdHeader: !!storedUserId,
        });

        const resp = await fetch(`${apiUrl}/api/social/events/accept-invite`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ token, eventId }),
        });

        const data = await resp.json().catch(() => ({}));

        if (!resp.ok) {
          throw new Error(
            data.message || t('socialJoinEvent.cannotJoin')
          );
        }

        setStatus('success');
        setMessage(data.message || t('socialJoinEvent.joinedSuccess'));
      } catch (err) {
        console.error('[SocialJoinEvent] accept error', err);
        setStatus('error');

        const msg = (err.message || '').toLowerCase();
        if (msg.includes('no autenticado')) {
          setMessage(
            t('socialJoinEvent.invalidSession')
          );
        } else {
          setMessage(
            err.message || t('socialJoinEvent.processingError')
          );
        }
      }
    };

    acceptInvite();
  }, [token, eventId, userRole, storedUserId, apiUrl]);

  const isLoggedIn = !!userRole;

  const loginRedirect = `/login?redirect=${encodeURIComponent(
    `/social/join-event?token=${token}&eventId=${eventId}`
  )}`;

  const registerRedirect = `/register?redirect=${encodeURIComponent(
    `/social/join-event?token=${token}&eventId=${eventId}`
  )}`;

  // ✅ Links útiles (sin “vínculo roto”)
  const osmLink =
    preview && typeof preview.lat === 'number' && typeof preview.lon === 'number'
      ? `https://www.openstreetmap.org/?mlat=${preview.lat}&mlon=${preview.lon}#map=15/${preview.lat}/${preview.lon}`
      : '';

  const gpxDownload =
    preview && preview.routeGpxUrl
      ? preview.routeGpxUrl.startsWith('http')
        ? preview.routeGpxUrl
        : `${apiUrl}${preview.routeGpxUrl}`
      : '';

  // ✅ AJUSTA ESTE PATH a tu “Comunidad Social” real si es distinto
  const COMMUNITY_HOME = '/social'; // <- si tu comunidad está en otra ruta, cámbiala aquí

  // ✅ App móvil (app.tzone.run): esta pantalla de "aceptar invitación"
  // solo existe aquí en la web, así que cualquiera que gestione una
  // invitación termina en tzone.run aunque normalmente use la app. Una
  // vez aceptada la invitación, le ofrecemos pasar a la app - detectamos
  // si el dispositivo parece movil (heuristica simple por user agent, no
  // hay forma real de saber si tiene la app instalada desde otro
  // dominio) y si es asi lo llevamos automaticamente despues de unos
  // segundos, dejando siempre la opcion de quedarse en la web.
  const appEventUrl = eventId ? `https://app.tzone.run/meetups/${eventId}` : 'https://app.tzone.run';
  const isMobileDevice =
    typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
  const [autoRedirectCancelled, setAutoRedirectCancelled] = useState(false);
  const [autoRedirectSecondsLeft, setAutoRedirectSecondsLeft] = useState(5);

  useEffect(() => {
    if (status !== 'success' || !isMobileDevice || autoRedirectCancelled) return undefined;

    setAutoRedirectSecondsLeft(5);
    const tick = setInterval(() => {
      setAutoRedirectSecondsLeft((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);
    const redirectTimer = setTimeout(() => {
      window.location.href = appEventUrl;
    }, 5000);

    return () => {
      clearInterval(tick);
      clearTimeout(redirectTimer);
    };
  }, [status, isMobileDevice, autoRedirectCancelled, appEventUrl]);

  return (
    <div className="home-root">
      <div className="home-overlay" />

      <div className="home-container">
        <main className="home-main">
          <img src={logo} alt="Tzone" className="home-logo" />

          <h1 className="home-title">{t('socialJoinEvent.pageTitle')}</h1>

          <p className="home-subtitle" style={{ maxWidth: 720 }}>
            {t('socialJoinEvent.intro')}
            {isLoggedIn
              ? status === 'success'
                ? ' ' + t('socialJoinEvent.attendanceConfirmed')
                : ' ' + t('socialJoinEvent.confirmingAttendance')
              : ' ' + t('socialJoinEvent.loginRequired')}
          </p>

          {/* ✅ VISTA PREVIA (SIN LOGIN) */}
          <div
            style={{
              marginTop: 18,
              width: 'min(920px, 95vw)',
              padding: '14px 14px',
              borderRadius: 16,
              background: 'rgba(0,0,0,0.30)',
              border: '1px solid rgba(255,255,255,0.10)',
              color: '#f9fafb',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              {t('socialJoinEvent.previewTitle')}
            </div>

            {previewError && (
              <div style={{ opacity: 0.9 }}>
                {previewError}
              </div>
            )}

            {preview && (
              <>
                <div style={{ marginBottom: 10, opacity: 0.95 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{preview.title}</div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    {t('socialJoinEvent.group')}: {preview.groupName || '—'} ·{' '}
                    {t('socialJoinEvent.date')}: {preview.dateTimeText ? new Date(preview.dateTimeText).toLocaleString() : '—'}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    {[
                      preview.locationName,
                      preview.commune,
                      preview.city,
                      preview.country,
                    ].filter(Boolean).join(', ')}
                  </div>

                  {(osmLink || gpxDownload) && (
                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      {osmLink && (
                        <>
                          <a
                            href={osmLink}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#60a5fa' }}
                          >
                            {t('socialJoinEvent.osmLink')}
                          </a>
                          {' · '}
                        </>
                      )}
                      {gpxDownload && (
                        <a
                          href={gpxDownload}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#60a5fa' }}
                        >
                          {t('socialJoinEvent.gpxDownload')}
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Mapa del encuentro */}
                <div style={{ marginTop: 10, fontWeight: 700, fontSize: 13 }}>
                  {t('socialJoinEvent.meetingMap')}
                </div>
                <div
                  ref={meetingMapElRef}
                  style={{
                    marginTop: 8,
                    height: 220,
                    borderRadius: 14,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.06)',
                  }}
                />

                {/* Mapa del recorrido */}
                {(Array.isArray(preview.routeGpxPoints) && preview.routeGpxPoints.length > 0) && (
                  <>
                    <div style={{ marginTop: 14, fontWeight: 700, fontSize: 13 }}>
                      {t('socialJoinEvent.routeMap')}
                    </div>
                    <div
                      ref={routeMapElRef}
                      style={{
                        marginTop: 8,
                        height: 240,
                        borderRadius: 14,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.06)',
                      }}
                    />
                  </>
                )}

                {(Array.isArray(preview.routeGpxPoints) && preview.routeGpxPoints.length === 0) && (
                  <div style={{ marginTop: 12, fontSize: 12, opacity: 0.9 }}>
                    {t('socialJoinEvent.noGpxPoints')}
                  </div>
                )}
              </>
            )}
          </div>

          {!isLoggedIn && (
            <div className="home-buttons" style={{ marginTop: 18 }}>
              <button
                className="btn-login"
                onClick={() => navigate(loginRedirect)}
              >
                {t('socialJoinEvent.login')}
              </button>
              <button
                className="btn-register"
                onClick={() => navigate(registerRedirect)}
              >
                {t('socialJoinEvent.createAccount')}
              </button>
            </div>
          )}

          {status !== 'idle' && (
            <div
              style={{
                marginTop: '24px',
                padding: '12px 18px',
                borderRadius: '10px',
                background:
                  status === 'success'
                    ? 'rgba(16, 185, 129, 0.15)'
                    : 'rgba(248, 113, 113, 0.15)',
                color: '#f9fafb',
                maxWidth: 720,
              }}
            >
              {message}
            </div>
          )}

          {status === 'success' && isMobileDevice && !autoRedirectCancelled && (
            <div
              style={{
                marginTop: 16,
                padding: '10px 14px',
                borderRadius: 10,
                background: 'rgba(250, 204, 21, 0.15)',
                color: '#f9fafb',
                maxWidth: 720,
                fontSize: 13,
              }}
            >
              Te vamos a llevar a la app de Tzone en {autoRedirectSecondsLeft} segundos…{' '}
              <button
                onClick={() => setAutoRedirectCancelled(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#60a5fa',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 13,
                }}
              >
                Quedarme viendo esto en la web
              </button>
            </div>
          )}

          {status === 'success' && (
            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {/* ✅ Para quienes usan la app: pasar directo al mismo encuentro ahí */}
              <button
                className="btn-community"
                onClick={() => {
                  window.location.href = appEventUrl;
                }}
              >
                Abrir en la app de Tzone
              </button>

              {/* ✅ lo que tú pediste: ir a Comunidad Social (NO al detalle) */}
              <button
                className="btn-community"
                onClick={() => navigate(COMMUNITY_HOME)}
              >
                {t('socialJoinEvent.backToCommunity')}
              </button>

              {/* opcional: si igual quieres conservar el acceso al detalle */}
              <button
                className="btn-login"
                onClick={() => navigate(`/social/meetups/${eventId}`)}
              >
                {t('socialJoinEvent.viewEventDetail')}
              </button>
            </div>
          )}
        </main>

        <footer className="home-footer">
          <div className="home-footer-line">
            © 2025 TZONE Corp SpA · RUT 77.923.319-7 · Lo Barnechea, Santiago de
            Chile ·{' '}
            <a href="mailto:contacto@tzone.run">contacto@tzone.run</a>
          </div>
          <div className="home-legal-links">
            <Link to="/legal/privacidad">Política de privacidad</Link>
            <span>·</span>
            <Link to="/legal/privacy">Privacy Policy</Link>
            <span>·</span>
            <Link to="/legal/aviso-metricas">
              Aviso sobre métricas y marcas registradas
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SocialJoinEventPage;
